import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  HStack,
  Text,
  Box,
} from '@chakra-ui/react';
import { Share2, UserPlus } from 'lucide-react';
import { ShareTab } from './components/ShareTab';
import { InviteMembersTab } from './components/InviteMembersTab';
import { usePersonalListShare } from './hooks/usePersonalListShare';
import type { ShareListModalProps, PersonalListShareProps, CollaborativeListShareProps } from './types';
import { isPersonalListProps } from './types';

/**
 * PersonalListContent - Content for personal list sharing
 */
function PersonalListContent({
  listName,
  urlKey,
  isOpen,
  isNotesPublic,
}: {
  listName: string;
  urlKey: string;
  isOpen: boolean;
  isNotesPublic?: boolean;
}) {
  const { shareUrl, shareWithNotes, isSharing, handleNotesToggle } = usePersonalListShare({
    urlKey,
    isOpen,
    isNotesPublic,
  });

  return (
    <ShareTab
      listName={listName}
      shareUrl={shareUrl}
      listType="personal"
      shareWithNotes={shareWithNotes}
      onNotesToggle={handleNotesToggle}
      isLoading={isSharing}
    />
  );
}

/**
 * CollaborativeListContent - Content for collaborative list invitations
 * Note: Share link tab disabled until public preview is implemented
 */
function CollaborativeListContent({
  urlKey,
  existingMembers,
  maxMembers,
  onClose,
}: {
  urlKey: string;
  existingMembers: CollaborativeListShareProps['existingMembers'];
  maxMembers: number;
  onClose: () => void;
}) {
  return (
    <InviteMembersTab
      urlKey={urlKey}
      existingMembers={existingMembers}
      maxMembers={maxMembers}
      onInviteComplete={onClose}
    />
  );
}

/**
 * ShareListModal - Unified modal for sharing personal and collaborative lists
 *
 * Features:
 * - Personal lists: Share with/without notes toggle
 * - Collaborative lists: Share link + Invite members tabs
 * - Purple theme throughout
 * - Haptics, native share API, copy link, SMS sharing
 */
export function ShareListModal(props: ShareListModalProps) {
  const { isOpen, onClose, listName } = props;
  const isPersonal = isPersonalListProps(props);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="purple.100" borderRadius="var(--r-inner)" p={2}>
              {isPersonal ? (
                <Share2 size={20} color="var(--chakra-colors-purple-500)" />
              ) : (
                <UserPlus size={20} color="var(--chakra-colors-purple-500)" />
              )}
            </Box>
            <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
              {isPersonal ? 'Share List' : 'Invite Members'}
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          {isPersonal ? (
            <PersonalListContent
              listName={listName}
              urlKey={(props as PersonalListShareProps).urlKey}
              isOpen={isOpen}
              isNotesPublic={(props as PersonalListShareProps).isNotesPublic}
            />
          ) : (
            <CollaborativeListContent
              urlKey={(props as CollaborativeListShareProps).urlKey}
              existingMembers={(props as CollaborativeListShareProps).existingMembers}
              maxMembers={(props as CollaborativeListShareProps).maxMembers}
              onClose={onClose}
            />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ShareListModal;
